import { useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import Modal from '../Modal'
import InputField from '../InputField'
import { 
    CurrencyCodeString,
    PayoutCreditFacilityModel,
    UpdatePayoutCreditFacilityModel
} from '../../redux/types'
import DatePicker from '../DatePicker'
import S1 from '../typography/S1'
import Select from '../Select'
import TextAreaField from '../TextAreaField'

interface Props {
    initialValue?: PayoutCreditFacilityModel
    onSaveClick?: (updateModel: UpdatePayoutCreditFacilityModel) => void
    onCancelClick?: () => void
    visible: boolean
}

const EditPayoutCreditFacilityModal = ({
    initialValue,
    onSaveClick,
    onCancelClick,
    visible
}: Props) => {
    const styles = useStyles()
    const [updateModel, setUpdateModel] = useState<UpdatePayoutCreditFacilityModel>()
    const [allowedOriginsText, setAllowedOriginsText] = useState('')

    // The update endpoint replaces the facility wholesale rather than patching it, so every field the server
    // reads must be seeded here. A field left out of this object is sent as undefined, binds to its default
    // server-side and silently overwrites the stored value. Add new fields here as well as to the form below.
    useEffect(() => {
        if (initialValue) {
            setUpdateModel({
                creditLimit: initialValue.creditLimit?.amount ?? 0,
                remainingCreditSpaceWarningPercent: initialValue.remainingCreditSpaceWarningPercent,
                launchDate: initialValue.launchDate,
                currency: initialValue.currency,
                maximumPayoutAmount: initialValue.maximumPayoutAmount,
                minimumPayoutAmount: initialValue.minimumPayoutAmount,
                minimumDaysUntilRepaymentDate: initialValue.minimumDaysUntilRepaymentDate,
                maximumDaysUntilRepaymentDate: initialValue.maximumDaysUntilRepaymentDate,
                maxCreditPerUser: initialValue.maxCreditPerUser,
                allowedOrigins: initialValue.allowedOrigins ?? []
            })
            setAllowedOriginsText((initialValue.allowedOrigins ?? []).join('\n'))
        }
    }, [initialValue])

    const handleOnSave = () => {
        if (updateModel) {
            onSaveClick && onSaveClick(updateModel)
        }
    }

    const handleOnClose = () => {
        onCancelClick && onCancelClick()
    }

    const handleOnChange = (propName: string, value: any) => {
        if (updateModel) {
            setUpdateModel({...updateModel, [propName]: value === '' ? null : value})
        }
    }

    const handleAllowedOriginsChange = (value: string) => {
        const allowedOrigins = value
            .split(/[\n,]/)
            .map(origin => origin.trim())
            .filter(origin => origin.length > 0)
        setAllowedOriginsText(value)
        handleOnChange('allowedOrigins', allowedOrigins)
    }

    const getCurrencyOptions = () => {
        return Object.keys(CurrencyCodeString).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    return (
        <Modal
            title='Edit payout credit facility'
            show={visible}
            okButtonTitle='Save'
            cancelButtonTitle='Cancel'
            onHide={() => handleOnClose()}
            onOkClick={() => handleOnSave()}
            onCancelClick={() => handleOnClose()}
        >
            <S1 className={styles.sectionTitle}>General</S1>
            <Select
                id='currency'
                key='currency'
                label='Currency'
                options={getCurrencyOptions()}
                selectedValue={updateModel?.currency.toString()}
                onSelect={(value) => handleOnChange('currency', value)}
            />
            <DatePicker
                id='launchDate'
                label='Launch date'
                includeTime={true}
                value={updateModel?.launchDate ?? undefined}
                onChange={value => {
                    handleOnChange('launchDate', value)
                }}
            />
            <TextAreaField
                id='allowedOrigins'
                value={allowedOriginsText}
                label='Allowed origins'
                rows={3}
                onChange={(event) => handleAllowedOriginsChange(event.target.value)}
                placeholder=''
            />
            <div style={{marginTop: 30}}>
                <S1 className={styles.sectionTitle}>Payout settings</S1>
                <InputField
                    id='minimumPayoutAmount'
                    value={updateModel?.minimumPayoutAmount?.toString() ?? ''}
                    label='Min. payout amount'
                    onChange={(event) => handleOnChange('minimumPayoutAmount', event.target.value)}
                    placeholder=''
                />
                <InputField
                    id='maximumPayoutAmount'
                    value={updateModel?.maximumPayoutAmount?.toString() ?? ''}
                    label='Max. payout amount'
                    onChange={(event) => handleOnChange('maximumPayoutAmount', event.target.value)}
                    placeholder=''
                />
                <InputField
                    id='minimumDaysUntilRepaymentDate'
                    numeric={true}
                    value={updateModel?.minimumDaysUntilRepaymentDate?.toString() ?? ''}
                    label='Min. days until repayment date'
                    onChange={(event) => handleOnChange('minimumDaysUntilRepaymentDate', event.target.value)}
                    placeholder=''
                />
                <InputField
                    id='maximumDaysUntilRepaymentDate'
                    numeric={true}
                    value={updateModel?.maximumDaysUntilRepaymentDate?.toString() ?? ''}
                    label='Max. days until repayment date'
                    onChange={(event) => handleOnChange('maximumDaysUntilRepaymentDate', event.target.value)}
                    placeholder=''
                />
                <InputField
                    id='maxCreditPerUser'
                    value={updateModel?.maxCreditPerUser?.toString() ?? ''}
                    label='Max. credit per user'
                    onChange={(event) => handleOnChange('maxCreditPerUser', event.target.value)}
                    placeholder=''
                />
            </div>
            <div style={{marginTop: 30}}>
                <S1 className={styles.sectionTitle}>Credit settings</S1>
                <InputField
                    id='creditLimit'
                    value={updateModel?.creditLimit?.toString() ?? ''}
                    label={`Credit limit (${initialValue?.creditUsage?.currencyCode?.toUpperCase()})`}
                    onChange={(event) => handleOnChange('creditLimit', event.target.value)}
                    placeholder=''
                />
                <InputField
                    id='remainingCreditSpaceWarningPercent'
                    numeric={true}
                    value={updateModel?.remainingCreditSpaceWarningPercent?.toString() ?? ''}
                    onChange={(event) => handleOnChange('remainingCreditSpaceWarningPercent', event.target.value)}
                    label='Remaining credit space warning percent'
                    placeholder=''
                />
            </div>
        </Modal>
    )
}

export default EditPayoutCreditFacilityModal

const useStyles = createUseStyles({
    container: {
        alignItems: 'center',
        textAlign: 'center',
        padding: '12px 20px',
        margin: '10px 0',
    },
    sectionTitle: {
        marginBottom: 15
    }
})
